import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import DefaultSlideShowScreen from "./components/DefaultSlideShowScreen";
import ErrorBoundary from "./components/ErrorBoundary";
import LoadingContainer from "./components/LoadingConitainer";
import OrganizationScreen from "./components/OrganizationScreen";
import PlaygroundBadge from "./components/PlaygroundBadge";
import TizenTestEnvironment from "./components/TizenTestEnvironment";
import LogsContainer from "./components/ui/LogsContainer";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { FullScreenProvider } from "./hooks/useFullScreen/full-screen-provider";
import { LogsProvider, useLogs } from "./hooks/useLogs";
import { useTizenTestMode } from "./hooks/useTizenTestMode";
import isInsideIframe from "./utils/isInsideIframe";
import ExitPopupDialog from "./components/ExitPopupDialog";

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <FullScreenProvider>
        <LogsProvider
          targetSequence={[
            "ArrowLeft",
            "ArrowRight",
            "ArrowRight",
            "ArrowLeft",
            "ArrowRight",
          ]}
        >
          <Main />
        </LogsProvider>
      </FullScreenProvider>
    </QueryClientProvider>
  );
}

function Main() {
  const { isTestMode, exitTestMode } = useTizenTestMode();
  const { log } = useLogs()
  useEffect(() => {
    if (isInsideIframe()) {
      window.parent.postMessage("app_loaded", "*");
      // @ts-expect-error
      log({ name: "AppLoaded", eventtype: "AppLoadedEvent", payload: {} })
    }
  }, [])

  useEffect(() => {
    const { outerWidth, outerHeight, innerWidth, innerHeight, screen: { width, height, availWidth, availHeight, orientation: { type, angle } } } = window
    log({
      // @ts-ignore
      name: "DeviceSizeInfo", eventtype: "ScreenSizeEvent",
      payload: {
        outerWidth, outerHeight, innerWidth, innerHeight,
        screen: JSON.stringify({ width, height, availWidth, availHeight, orientation: { type, angle } }),
      },
    })
  }, [])

  return (
    <div
      aria-hidden="true"
      className="relative"
      tabIndex={-1}
      id="rootElement"
    >
      {isTestMode && <PlaygroundBadge />}
      <AuthProvider>
        <ErrorBoundary>
          <div aria-hidden="true" className="absolute">
            {isTestMode && !isInsideIframe() ? (
              <TizenTestEnvironment onClose={exitTestMode} />
            ) : (
              <Home />
            )}
          </div>
        </ErrorBoundary>
      </AuthProvider>
    </div>
  );
}

function Home() {
  const { log } = useLogs()
  const { screenInfoFetchStatus, screenInfo, isPreviewMode } = useAuth();

  const hasPaired = screenInfo !== null && !isPreviewMode

  useEffect(() => {
    // @ts-expect-error
    log({ eventtype: "Journey", name: "Home", payload: { hasPaired, isPreviewMode, screenInfoFetchStatus, hasScreenInfo: screenInfo !== null } })
  }, [screenInfoFetchStatus, screenInfo, isPreviewMode])

  return (
    <div aria-hidden="true" className="h-screen w-screen">
      {screenInfoFetchStatus !== "success" ? (
        <LoadingContainer />
      ) : (
        hasPaired ? (
          <OrganizationScreen initialScreenInfo={screenInfo} key={screenInfo.id} mountExitPopup />
        ) : (
          <ExitPopupDialog hasError={false} isLoading={false} isPaired mountIfError={false} musicEnabled={false}>
            <DefaultSlideShowScreen />
          </ExitPopupDialog>
        )
      )}
      <LogsContainer />
    </div>
  );
}

export default App;
